import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  LinearProgress,
  Tooltip
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { ProdutividadeUsuarioDetalhada } from '../../services/relatorioService';

interface ProdutividadeDetalhadaProps {
  dados: ProdutividadeUsuarioDetalhada[];
  titulo?: string;
}

const ProdutividadeDetalhada: React.FC<ProdutividadeDetalhadaProps> = ({ 
  dados, 
  titulo = 'Top Usuários Mais Produtivos' 
}) => {
  const obterCorScore = (score: number): 'success' | 'warning' | 'error' => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const obterMedalha = (posicao: number): string => {
    if (posicao === 0) return '🥇';
    if (posicao === 1) return '🥈';
    if (posicao === 2) return '🥉';
    return `${posicao + 1}º`;
  };

  const formatarTempo = (dias: string | number): string => {
    const diasNum = typeof dias === 'string' ? parseFloat(dias) : dias;
    if (diasNum === 0) return 'N/A';
    if (diasNum < 1) return `${Math.round(diasNum * 24)}h`;
    if (diasNum === 1) return '1 dia';
    return `${Math.round(diasNum)} dias`;
  };

  if (!dados || dados.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <EmojiEventsIcon sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="h6">{titulo}</Typography>
          </Box>
          <Typography color="textSecondary">Nenhum dado disponível</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EmojiEventsIcon sx={{ mr: 1, color: 'warning.main' }} />
          <Typography variant="h6">{titulo}</Typography>
        </Box>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="60"><strong>Pos.</strong></TableCell>
                <TableCell><strong>Usuário</strong></TableCell>
                <TableCell><strong>Departamento</strong></TableCell>
                <TableCell align="center"><strong>Concluídas</strong></TableCell>
                <TableCell align="center"><strong>Ativas</strong></TableCell>
                <TableCell align="center"><strong>Atrasadas</strong></TableCell>
                <TableCell align="center"><strong>Tempo Médio</strong></TableCell>
                <TableCell align="center" width="180"><strong>Score</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dados.map((usuario, index) => (
                <TableRow key={usuario.usuarioId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {obterMedalha(index)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {usuario.nome}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {usuario.departamento}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={usuario.tarefasConcluidas} 
                      size="small" 
                      color="success"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={usuario.tarefasAtivas} 
                      size="small" 
                      color="info"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {usuario.tarefasAtrasadas > 0 ? (
                      <Chip 
                        label={usuario.tarefasAtrasadas} 
                        size="small" 
                        color="error"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="textSecondary">
                      {formatarTempo(usuario.tempoMedioConclusao)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={`Taxa: ${usuario.taxaConclusao}% | Score: ${usuario.produtividadeScore}`}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={usuario.produtividadeScore} 
                            color={obterCorScore(usuario.produtividadeScore)}
                            sx={{ height: 8, borderRadius: 1 }}
                          />
                        </Box>
                        <Typography variant="body2" fontWeight="bold" minWidth="35px">
                          {usuario.produtividadeScore}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default ProdutividadeDetalhada;
